import {
  Accordion, Alert, Badge, Box, Button, Card, ColorInput, Grid, Group, Loader, Modal, NumberInput, ScrollArea, SegmentedControl,
  Select, SimpleGrid, Slider, Stack, Switch, Table, Tabs, Text, Textarea, TextInput,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle, IconCode, IconExternalLink, IconHistory, IconLayoutBoard, IconPlus, IconRestore, IconWand,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type ReactNode } from "react";

import BlocksEditor, { type Block } from "./TemplateBlocksEditor";

import { authFetch, errorMessage } from "../../api/client";
import { http } from "../../api/http";
import { fmtDateTime, PageHeader } from "../../components/ui";
import { useT } from "../../i18n/script";
import AdminLayout from "./AdminLayout";

/** Izgled iz vizuelnog graditelja (isti oblik kao `Design` u backend/app/modules/reports/template_builder.py). */
interface Design {
  blocks: Block[];
  font: "carlito" | "sans" | "serif"; font_size: number;
  margin_top: number; margin_right: number; margin_bottom: number; margin_left: number;
  header_align: "center" | "left"; show_emblem: boolean; show_agency_logo: boolean; logo_height: number;
  show_parent: boolean; show_ministry: boolean; show_address: boolean; header_rule: boolean;
  show_reg_no: boolean; reg_label: string; date_label: string; date_suffix: string;
  title_size: number; title_upper: boolean; title_align: "center" | "left"; accent: string;
  table_style: "grid" | "horizontal" | "minimal"; table_header_fill: boolean; table_zebra: boolean; table_font_size: number;
  page_numbers: "none" | "right" | "center"; page_format: "n" | "n_of_m"; footer_text: string; watermark: string;
}
interface H { id: number; code: string; version: number; name: string; active: boolean; created_by: string; created_at: string; size: number; builder: boolean }
interface Tpl { code: string; version: number; name: string; body: string; design: Design | null; modified: boolean }

/** Šta se uređuje: izgled kroz formu (HTML generiše server) ili ručno pisan HTML. */
type Mode = "design" | "code";

const API = "/api/v1/reports/html-templates";

export default function HtmlTemplatesPage() {
  const { t } = useT();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["html-tpls"], queryFn: () => http.get<H[]>(API) });
  const defaults = useQuery({ queryKey: ["html-tpl-defaults"], queryFn: () => http.get<Design>(`${API}/design-defaults`), staleTime: Infinity });
  const [code, setCode] = useState("standard");
  const cur = useQuery({ queryKey: ["html-tpl", code], queryFn: () => http.get<Tpl>(`${API}/${code}`) });

  const [mode, setMode] = useState<Mode>("design");
  const [design, setDesign] = useState<Design | null>(null);
  const [body, setBody] = useState("");
  const [name, setName] = useState("");
  const [dirty, setDirty] = useState(false);
  const [tab, setTab] = useState<string | null>("izgled");

  const load = (tp: Tpl) => {
    setName(tp.name);
    setBody(tp.body);
    if (tp.design && !tp.modified) { setMode("design"); setDesign(tp.design); } else { setMode("code"); setDesign(tp.design); }
    setDirty(false);
  };
  useEffect(() => { if (cur.data) load(cur.data); }, [cur.data]);

  const set = <K extends keyof Design>(k: K, v: Design[K]) => { setDesign((d) => (d ? { ...d, [k]: v } : d)); setDirty(true); };

  const versions = useMemo(() => (list.data ?? []).filter((x) => x.code === code), [list.data, code]);
  const codes = useMemo(() => {
    const m = new Map<string, H>();
    (list.data ?? []).forEach((x) => { if (!m.has(x.code)) m.set(x.code, x); });
    return [...m.values()];
  }, [list.data]);

  const save = useMutation({
    mutationFn: (c: string) => http.put<{ version: number }>(`${API}/${c}`, mode === "design" ? { name, design } : { name, body }),
    onSuccess: (r, c) => {
      qc.invalidateQueries({ queryKey: ["html-tpls"] }); qc.invalidateQueries({ queryKey: ["html-tpl", c] });
      setDirty(false);
      notifications.show({ color: "green", message: `${t("Sačuvana verzija")} ${r.version}` });
    },
    onError: (e: Error) => notifications.show({ color: "red", title: t("Šablon nije sačuvan"), message: e.message }),
  });

  // nova kopija trenutnog šablona pod drugim kodom
  const [newOpen, setNewOpen] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [newName, setNewName] = useState("");
  const create = useMutation({
    mutationFn: () => http.put<{ version: number }>(`${API}/${newCode}`, mode === "design" ? { name: newName, design } : { name: newName, body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["html-tpls"] }); setNewOpen(false); setCode(newCode); setNewCode(""); setNewName("");
      notifications.show({ color: "green", message: t("Novi šablon je napravljen") });
    },
    onError: (e: Error) => notifications.show({ color: "red", message: e.message }),
  });

  const toDesign = () => { setDesign(design ?? defaults.data ?? null); setMode("design"); setDirty(true); setTab("izgled"); };
  const toCode = async () => {
    if (mode === "design" && design) {
      try { setBody((await http.post<{ body: string }>(`${API}/build`, design)).body); } catch (e) { notifications.show({ color: "red", message: (e as Error).message }); return; }
    }
    setMode("code"); setDirty(true);
  };
  const openVersion = async (v: number) => {
    try { load(await http.get<Tpl>(`${API}/${code}/versions/${v}`)); setDirty(true); setTab("izgled"); }
    catch (e) { notifications.show({ color: "red", message: (e as Error).message }); }
  };

  const previewInput = mode === "design" ? { design } : { body };
  const [debounced] = useDebouncedValue(previewInput, 600);

  return (
    <AdminLayout>
      <>
        <PageHeader title="Šabloni dokumenata"
          description="Izgled PDF dokumenata koje aplikacija izdaje: zaglavlje, font, tabele i podnožje. Promjene se odmah vide u pregledu; svako snimanje pravi novu verziju, a starije se mogu vratiti." />
        <Card withBorder mb="md" p="sm">
          <Group align="flex-end" wrap="wrap" gap="sm">
            <Select label={t("Šablon")} w={260} value={code} allowDeselect={false}
              data={codes.map((c) => ({ value: c.code, label: `${c.code} · v${c.version}` }))}
              onChange={(v) => { if (v && (!dirty || window.confirm(t("Odbaciti nesačuvane izmjene?")))) setCode(v); }} />
            <TextInput label={t("Naziv")} value={name} onChange={(e) => { setName(e.currentTarget.value); setDirty(true); }} style={{ flex: 1, minWidth: 220 }} />
            <Button variant="default" leftSection={<IconPlus size={16} />} onClick={() => { setNewName(`${name} (kopija)`); setNewOpen(true); }}>{t("Novi šablon")}</Button>
            <Button onClick={() => save.mutate(code)} loading={save.isPending} disabled={!dirty || (mode === "design" && !design)}>{t("Sačuvaj novu verziju")}</Button>
          </Group>
          <Text size="xs" c="dimmed" mt={6}>
            {t("Izvještaj koristi šablon izabran u graditelju izvještaja (podrazumijevano „standard“). Naziv Agencije, adresa, grb i logo se unose u Podešavanja → Izlazni dokumenti.")}
          </Text>
        </Card>

        <Grid>
          <Grid.Col span={{ base: 12, lg: 5 }}>
            <Tabs value={tab} onChange={setTab} keepMounted={false}>
              <Tabs.List mb="sm">
                <Tabs.Tab value="izgled" leftSection={<IconLayoutBoard size={15} />}>{t("Izgled")}</Tabs.Tab>
                <Tabs.Tab value="kod" leftSection={<IconCode size={15} />}>{t("HTML kod")}</Tabs.Tab>
                <Tabs.Tab value="verzije" leftSection={<IconHistory size={15} />}>{t("Verzije")} <Badge size="xs" variant="light" ml={4}>{versions.length}</Badge></Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="izgled">
                {mode === "code" ? (
                  <Alert color="yellow" icon={<IconAlertTriangle size={18} />} title={t("Šablon je pisan ručno")}>
                    <Text size="sm" mb="sm">{t("HTML ovog šablona nije napravljen u formi (ili je poslije ručno mijenjan), pa se izgled ne može podešavati kroz formu. Možete ga i dalje mijenjati u kartici „HTML kod“ ili preći na vizuelno uređivanje. Trenutni HTML ostaje u istoriji verzija.")}</Text>
                    <Button size="xs" leftSection={<IconWand size={15} />} onClick={toDesign} disabled={!design && !defaults.data}>
                      {design ? t("Uređuj kroz formu (vrati izgled iz forme)") : t("Uređuj kroz formu (polazi od standardnog izgleda)")}
                    </Button>
                  </Alert>
                ) : design ? (
                  <DesignForm d={design} set={set} onReset={() => { if (defaults.data) { setDesign(defaults.data); setDirty(true); } }} />
                ) : <Loader size="sm" />}
              </Tabs.Panel>

              <Tabs.Panel value="kod">
                {mode === "design" ? (
                  <Stack gap="xs">
                    <Text size="sm" c="dimmed">{t("Ovaj kod se pravi automatski iz forme „Izgled“ i ne treba ga mijenjati. Za izmjene koje forma ne nudi uključite ručno uređivanje. Prije snimanja sistem provjerava šablon na primjeru dokumenta i ne dozvoljava snimanje šablona koji ne radi.")}</Text>
                    <Group><Button size="xs" variant="default" leftSection={<IconCode size={15} />} onClick={toCode}>{t("Uređuj HTML ručno")}</Button></Group>
                    <GeneratedCode design={design} />
                  </Stack>
                ) : (
                  <Stack gap="xs">
                    <Text size="sm" c="dimmed">{t("Jinja2 HTML. Dostupno: doc (naslov, parametri, sekcije), f(vrijednost, kolona), chart(sekcija), org, org_name, datum. Šablon radi u zaštićenom režimu: ne može pristupiti sistemu ni podacima van dokumenta.")}</Text>
                    <Textarea value={body} onChange={(e) => { setBody(e.currentTarget.value); setDirty(true); }} autosize minRows={22} maxRows={40}
                      styles={{ input: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12 } }} spellCheck={false} />
                  </Stack>
                )}
              </Tabs.Panel>

              <Tabs.Panel value="verzije">
                <Table striped highlightOnHover fz="sm">
                  <Table.Thead><Table.Tr><Table.Th>{t("Verzija")}</Table.Th><Table.Th>{t("Naziv")}</Table.Th><Table.Th>{t("Autor")}</Table.Th><Table.Th>{t("Vrijeme")}</Table.Th><Table.Th /></Table.Tr></Table.Thead>
                  <Table.Tbody>
                    {versions.map((v) => (
                      <Table.Tr key={v.id}>
                        <Table.Td>v{v.version} {v.builder ? <Badge size="xs" variant="light" color="green">{t("forma")}</Badge> : <Badge size="xs" variant="light" color="gray">HTML</Badge>}</Table.Td>
                        <Table.Td>{v.name}</Table.Td><Table.Td>{v.created_by}</Table.Td><Table.Td>{fmtDateTime(v.created_at)}</Table.Td>
                        <Table.Td><Button size="compact-xs" variant="subtle" leftSection={<IconRestore size={14} />} onClick={() => openVersion(v.version)}>{t("Otvori")}</Button></Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
                <Text size="xs" c="dimmed" mt="xs">{t("„Otvori“ učitava stariju verziju u uređivanje; tek „Sačuvaj novu verziju“ je vraća u upotrebu.")}</Text>
              </Tabs.Panel>
            </Tabs>
          </Grid.Col>

          <Grid.Col span={{ base: 12, lg: 7 }}>
            <Preview input={debounced} ready={debounced.design ? true : !!debounced.body?.trim()} />
          </Grid.Col>
        </Grid>

        <Modal opened={newOpen} onClose={() => setNewOpen(false)} title={t("Novi šablon")}>
          <Stack>
            <Text size="sm">{t("Novi šablon počinje kao kopija trenutnog izgleda.")}</Text>
            <TextInput label={t("Kod")} description={t("mala slova, cifre i _; npr. potvrda_registracija")} value={newCode}
              onChange={(e) => setNewCode(e.currentTarget.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
              error={codes.some((c) => c.code === newCode) ? t("Šablon sa tim kodom već postoji") : undefined} />
            <TextInput label={t("Naziv")} value={newName} onChange={(e) => setNewName(e.currentTarget.value)} />
            <Group justify="flex-end"><Button loading={create.isPending} disabled={!newCode || !newName || codes.some((c) => c.code === newCode)} onClick={() => create.mutate()}>{t("Napravi")}</Button></Group>
          </Stack>
        </Modal>
      </>
    </AdminLayout>
  );
}

/* ------------------------------------------------------------------ forma izgleda */
function DesignForm({ d, set, onReset }: { d: Design; set: <K extends keyof Design>(k: K, v: Design[K]) => void; onReset: () => void }) {
  const { t } = useT();
  const num = (k: keyof Design, label: string, min: number, max: number, step = 1, suffix?: string) => (
    <NumberInput label={t(label)} value={d[k] as number} min={min} max={max} step={step} suffix={suffix} decimalScale={step < 1 ? 1 : 0} clampBehavior="strict"
      onChange={(v) => { if (typeof v === "number") set(k, v as never); }} />
  );
  const sw = (k: keyof Design, label: string, desc?: string) => (
    <Switch label={t(label)} description={desc ? t(desc) : undefined} checked={d[k] as boolean} onChange={(e) => set(k, e.currentTarget.checked as never)} />
  );
  const seg = (k: keyof Design, data: { value: string; label: string }[]) => (
    <SegmentedControl fullWidth size="xs" value={d[k] as string} onChange={(v) => set(k, v as never)} data={data.map((x) => ({ ...x, label: t(x.label) }))} />
  );
  const Lbl = ({ children }: { children: ReactNode }) => <Text size="sm" fw={500} mb={4}>{children}</Text>;
  return (
    <Stack gap="sm">
      <Accordion variant="separated" multiple defaultValue={["sadrzaj"]}>
        <Accordion.Item value="sadrzaj">
          <Accordion.Control>{t("Sadržaj dokumenta")}</Accordion.Control>
          <Accordion.Panel>
            <BlocksEditor blocks={d.blocks} onChange={(b) => set("blocks", b)} />
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="zaglavlje">
          <Accordion.Control>{t("Zaglavlje (memorandum)")}</Accordion.Control>
          <Accordion.Panel><Stack gap="sm">
            <Box><Lbl>{t("Poravnanje")}</Lbl>{seg("header_align", [{ value: "center", label: "Centrirano" }, { value: "left", label: "Lijevo" }])}</Box>
            <SimpleGrid cols={2}>{sw("show_emblem", "Grb")}{sw("show_agency_logo", "Logo Agencije")}</SimpleGrid>
            <Box><Lbl>{t("Visina grba i logoa")}: {d.logo_height} mm</Lbl>
              <Slider min={10} max={40} value={d.logo_height} onChange={(v) => set("logo_height", v)} marks={[{ value: 10 }, { value: 24 }, { value: 40 }]} label={(v) => `${v} mm`} /></Box>
            {sw("show_parent", "Red „Republika Srpska“")}
            {sw("show_ministry", "Red ministarstva")}
            {sw("show_address", "Adresa, telefon, e-pošta i JIB")}
            {sw("header_rule", "Linija ispod zaglavlja")}
          </Stack></Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="broj">
          <Accordion.Control>{t("Broj i datum")}</Accordion.Control>
          <Accordion.Panel><Stack gap="sm">
            {sw("show_reg_no", "Prikaži djelovodni broj")}
            <SimpleGrid cols={3}>
              <TextInput label={t("Oznaka broja")} value={d.reg_label} maxLength={40} disabled={!d.show_reg_no} onChange={(e) => set("reg_label", e.currentTarget.value)} />
              <TextInput label={t("Oznaka datuma")} value={d.date_label} maxLength={40} onChange={(e) => set("date_label", e.currentTarget.value)} />
              <TextInput label={t("Iza datuma")} value={d.date_suffix} maxLength={20} onChange={(e) => set("date_suffix", e.currentTarget.value)} />
            </SimpleGrid>
          </Stack></Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="naslov">
          <Accordion.Control>{t("Naslov i boja")}</Accordion.Control>
          <Accordion.Panel><Stack gap="sm">
            <SimpleGrid cols={2}>{num("title_size", "Veličina naslova", 10, 20, 0.5, " pt")}<Box pt={26}>{sw("title_upper", "Velika slova")}</Box></SimpleGrid>
            <Box><Lbl>{t("Poravnanje naslova")}</Lbl>{seg("title_align", [{ value: "center", label: "Centrirano" }, { value: "left", label: "Lijevo" }])}</Box>
            <ColorInput label={t("Boja naslova, linija i zaglavlja tabele")} description={t("Akti Agencije su crno-bijeli; boja je za interne izvještaje.")}
              value={d.accent} format="hex" withEyeDropper={false} swatches={["#000000", "#1f4d36", "#2f6f4e", "#1d4e89", "#7a1f1f", "#555555"]}
              onChange={(v) => { if (/^#[0-9a-fA-F]{6}$/.test(v)) set("accent", v); }} />
          </Stack></Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="font">
          <Accordion.Control>{t("Font i margine")}</Accordion.Control>
          <Accordion.Panel><Stack gap="sm">
            <SimpleGrid cols={2}>
              <Select label={t("Font")} value={d.font} allowDeselect={false} onChange={(v) => v && set("font", v as Design["font"])}
                data={[{ value: "carlito", label: "Calibri (Carlito)" }, { value: "sans", label: t("Bez serifa (DejaVu Sans)") }, { value: "serif", label: t("Sa serifima (DejaVu Serif)") }]} />
              {num("font_size", "Veličina teksta", 9, 13, 0.5, " pt")}
            </SimpleGrid>
            <Text size="sm" fw={500}>{t("Margine stranice (mm)")}</Text>
            <SimpleGrid cols={4}>{num("margin_top", "Gore", 10, 40)}{num("margin_right", "Desno", 10, 40)}{num("margin_bottom", "Dolje", 10, 40)}{num("margin_left", "Lijevo", 10, 40)}</SimpleGrid>
          </Stack></Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="tabele">
          <Accordion.Control>{t("Tabele")}</Accordion.Control>
          <Accordion.Panel><Stack gap="sm">
            <Box><Lbl>{t("Linije")}</Lbl>{seg("table_style", [{ value: "grid", label: "Mreža" }, { value: "horizontal", label: "Samo vodoravne" }, { value: "minimal", label: "Minimalno" }])}</Box>
            <SimpleGrid cols={2}>{sw("table_header_fill", "Obojeno zaglavlje")}{sw("table_zebra", "Naizmjenični redovi")}</SimpleGrid>
            {num("table_font_size", "Veličina teksta u tabeli", 7.5, 11, 0.5, " pt")}
          </Stack></Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="podnozje">
          <Accordion.Control>{t("Podnožje i numeracija")}</Accordion.Control>
          <Accordion.Panel><Stack gap="sm">
            <Box><Lbl>{t("Broj stranice")}</Lbl>{seg("page_numbers", [{ value: "none", label: "Bez" }, { value: "right", label: "Desno" }, { value: "center", label: "Sredina" }])}</Box>
            {d.page_numbers !== "none" && <Box><Lbl>{t("Oblik")}</Lbl>{seg("page_format", [{ value: "n", label: "1" }, { value: "n_of_m", label: "Strana 1 od 3" }])}</Box>}
            <Textarea label={t("Tekst u podnožju stranice")} description={t("Sitnim slovima na dnu svake stranice, lijevo od broja strane. Tekst u samom dokumentu dodajte blokom „Tekst“ u sadržaju.")} value={d.footer_text} maxLength={400} autosize minRows={2}
              onChange={(e) => set("footer_text", e.currentTarget.value)} />
            <TextInput label={t("Vodeni žig")} description={t("Npr. NACRT ili PRIMJERAK; prazno = bez žiga.")} value={d.watermark} maxLength={30}
              onChange={(e) => set("watermark", e.currentTarget.value)} />
          </Stack></Accordion.Panel>
        </Accordion.Item>
      </Accordion>
      <Group justify="flex-end"><Button size="xs" variant="subtle" color="gray" leftSection={<IconRestore size={15} />} onClick={onReset}>{t("Vrati standardni izgled Agencije")}</Button></Group>
    </Stack>
  );
}

/* ------------------------------------------------------------------ kod iz forme (samo za čitanje) */
function GeneratedCode({ design }: { design: Design | null }) {
  const [debounced] = useDebouncedValue(design, 400);
  const q = useQuery({ queryKey: ["html-tpl-build", debounced], enabled: !!debounced,
    queryFn: () => http.post<{ body: string }>(`${API}/build`, debounced) });
  return (
    <ScrollArea h={560} type="auto" style={{ border: "1px solid var(--mantine-color-default-border)", borderRadius: 8 }}>
      <Box component="pre" m={0} p="sm" fz={11} style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
        {q.data?.body ?? ""}
      </Box>
    </ScrollArea>
  );
}

/* ------------------------------------------------------------------ pregled: PDF primjera dokumenta */
function Preview({ input, ready }: { input: { design?: Design | null; body?: string }; ready: boolean }) {
  const { t } = useT();
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const key = JSON.stringify(input);
  useEffect(() => {
    if (!ready) return;
    let alive = true;
    const ctl = new AbortController();
    setBusy(true);
    authFetch(`${API}/preview`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...input, format: "pdf" }), signal: ctl.signal })
      .then(async (r) => {
        if (!r.ok) {
          const txt = await r.text();
          let msg = txt;
          try { msg = errorMessage(JSON.parse(txt)); } catch { /* tekst */ }
          throw new Error(msg || r.statusText);
        }
        return r.blob();
      })
      .then((b) => {
        if (!alive) return;
        setErr(null);
        setUrl((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(b); });
      })
      .catch((e: Error) => { if (alive && e.name !== "AbortError") setErr(e.message); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; ctl.abort(); };
  }, [key, ready]);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  return (
    <Card withBorder p="sm" style={{ position: "sticky", top: 64 }}>
      <Group justify="space-between" mb="xs">
        <Group gap={6}><Text fw={600}>{t("Pregled")}</Text><Text size="xs" c="dimmed">{t("primjer dokumenta sa izmišljenim podacima")}</Text>{busy && <Loader size={14} />}</Group>
        <Button size="compact-xs" variant="subtle" disabled={!url} leftSection={<IconExternalLink size={14} />} onClick={() => url && window.open(url, "_blank")}>{t("Otvori u novom prozoru")}</Button>
      </Group>
      {err && <Alert color="red" icon={<IconAlertTriangle size={18} />} mb="xs" title={t("Šablon trenutno ne radi")}>{err}</Alert>}
      {url
        ? <iframe title={t("Pregled šablona")} src={`${url}#toolbar=0&navpanes=0&view=FitH`} style={{ width: "100%", height: "calc(100vh - 150px)", minHeight: 520, border: "1px solid var(--mantine-color-default-border)", borderRadius: 8, opacity: err ? .4 : 1 }} />
        : <Group justify="center" h={400}><Loader /></Group>}
    </Card>
  );
}
